import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { Observable, combineLatest, map, tap } from 'rxjs';
import { IDevice, IDeviceDetailsResponse } from 'src/app/devices/models/device';
import { IAddServicePart, IDeleteServicePart } from 'src/app/devices/models/service-part';
import { IUpdateWearingPartRequest } from 'src/app/devices/models/wearing-part';
import { ServicePartService } from 'src/app/devices/services/service-part.service';
import { WearingPartService } from 'src/app/devices/services/wearing-part.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { caretAltLeftIcon, caretAltToLeftIcon, caretAltRightIcon, caretAltToRightIcon } from '@progress/kendo-svg-icons';
import { DeviceService } from 'src/app/devices/services/device.service';

@Component({
    selector: 'app-revisions-detail-wearing-parts',
    templateUrl: './revisions-detail-wearing-parts.component.html',
    styleUrl: './revisions-detail-wearing-parts.component.scss',
    standalone: false
})
export class RevisionsDetailWearingPartsComponent {

  ////////////////
  // variables //
  //////////////

  @Input() revisionId;
  @Input() revisionStatus;

  vm$: Observable<any>;
  deviceIds = [];
  baseUrl: string;
  amount: number;

  // variables for pagination
  currentPage: number = 1;
  pageSize: number = 25;
  totalPages: number = 0;
  totalCount: number = 0;
  hasNextPage: boolean = false;
  hasPreviousPage: boolean = false;

  // Make icons available to template
  public caretAltLeftIcon = caretAltLeftIcon;
  public caretAltToLeftIcon = caretAltToLeftIcon;
  public caretAltRightIcon = caretAltRightIcon;
  public caretAltToRightIcon = caretAltToRightIcon;  


  constructor(
    private appnotificationService: AppNotificationService,
    private wearingPartService: WearingPartService,
    private servicePartService: ServicePartService,
    private deviceStore: DeviceStore,
    private deviceService: DeviceService,
    private router: Router
  ) {
    this.vm$ = combineLatest([ 
      this.deviceStore.devices$.pipe(
        tap((devices) => {
          devices.forEach(device => {
            device.wearingParts.forEach((wearingPart: any) => {
              // Check if the wearingPart is linked to a service
              const isLinked = device.services.some((service: any) =>
                service.serviceParts.some((part: any) => part.wearingPart.id === wearingPart.id)
              );

              // Set the initial state of the checkbox
              wearingPart.isChecked = isLinked;
            });

            // Sort: active products first, inactive last
            device.wearingParts.sort((a: any, b: any) => {
              if (a.product.isActive !== b.product.isActive) {
                return a.product.isActive ? -1 : 1;
              }
              return a.product.internalNumber.localeCompare(b.product.internalNumber);
            });
          });
        })
      ),
      this.deviceStore.pagination$.pipe(
        tap(pagination => {
          if (pagination) {
          this.pageSize = pagination.pageSize
          this.hasNextPage = pagination.hasNextPage
          this.hasPreviousPage = pagination.hasPreviousPage
          this.currentPage = pagination.currentPage
          this.totalPages = pagination.totalPages
          this.totalCount = pagination.totalCount
          }
        })
      ),
      this.deviceStore.loading$
    ]).pipe(
      map(([
        devices,
        devicePagination,
        loading
      ]) => ({
        devices,
        devicePagination,
        loading
      }))
    );
  }

  ngOnInit() {
    this.deviceStore.getDevicesWithWearingPartsByRevision({
      revisionId: this.revisionId,
      pageNumber: this.currentPage,
      pageSize: this.pageSize
    });
    
    this.baseUrl = this.router.url.split('/')[0] + '/devices-admin/';
  }

  exportWearingParts() {
    this.deviceService.getDevicesWithWearingPartsByRevision({
      revisionId: this.revisionId,
      pageNumber: 0,
      pageSize: 999999,
    }).subscribe((result) => {
      const hasAnySelected = result.data.some(device =>
        device.wearingParts.some(wearingPart => {
          if (!wearingPart.product.isActive) return false;
          return device.services.some(service =>
            service.serviceParts.some(part => part.wearingPart.id === wearingPart.id)
          );
        })
      );

      if (!hasAnySelected) {
        this.appnotificationService.notification$.next({
          message: translate('wearingParts.noneSelected'),
          style: 'info'
        });
        return;
      }

      this.wearingPartService.exportWearingPartsByRevision(this.revisionId);
    });
  }

  copyWearingParts() {
    if (navigator.clipboard) {

      this.deviceService.getDevicesWithWearingPartsByRevision({
        revisionId: this.revisionId,
        pageNumber: 0,
        pageSize: 999999,
      }).subscribe((result) => {

        const strippedData = result.data.flatMap(device =>
          device.wearingParts
            .filter(wearingPart => {
              // exclude inactive products
              if (!wearingPart.product.isActive) return false;
              // check if the wearingPart is linked in any service of this device
              const isLinked = device.services.some(service =>
                service.serviceParts.some(part => part.wearingPart.id === wearingPart.id)
              );
              return isLinked;
            })
            .map(wearingPart => ({
              sapNumber: wearingPart.product.internalNumber,
              quantity: wearingPart.quantity,
            }))
        );

        if (strippedData.length === 0) {
          this.appnotificationService.notification$.next({
            message: translate('wearingParts.noneSelected'),
            style: 'info'
          });
          return;
        }

        const rows = strippedData.map(part =>
          `${part.sapNumber}\t${part.quantity}`
        );

        const dataToCopy = rows.join('\n');

        navigator.clipboard.writeText(dataToCopy)
          .then(() => {
            this.appnotificationService.notification$.next({message: translate('wearingParts.copySucceeded'), style:'success'});
          })
          .catch(err => {
            this.appnotificationService.notification$.next({message: translate('wearingParts.copyFailed'), style:'error'});
          });

      });

    } else {
      console.error('Clipboard API not supported in this browser.');
    }
  }


  // add service part dialog
  public servicePartDialogOpened = false;
  public confirmUncheckDialogOpened = false;
  public dialogData: { wearingPartId?: string; device?: any; amount?: number } = {};
  public pendingDeleteData: { request: IDeleteServicePart; deviceId: string; wearingPart: any } | null = null;

  public servicePartDialogOpen(wearingPart: any, device: any, quantity: number): void {
    if (this.revisionStatus?.isFinalStatus == true) {
      return;
    }
    if (!wearingPart.product.isActive) {
      return;
    } else {
      this.dialogData = {
        wearingPartId: wearingPart.id,
        device: device,
        amount: wearingPart.quantity
      };
  
      if (!wearingPart.isChecked) {
        this.servicePartDialogOpened = true;
        this.amount = quantity;
      } else {
        if (this.dialogData.wearingPartId) {
          const servicePart = device.services
          .map(service =>
            service.serviceParts.find((part: any) => part.wearingPart.id === this.dialogData.wearingPartId)
          )
          .find(part => part !== undefined);

          if (servicePart) {
            var request: IDeleteServicePart = {
              id: servicePart.id,
              wearingPartId: wearingPart.id,
              deviceId: device.id,
              revisionId: this.revisionId
            }

            if (servicePart.isUsed) {
              // Show confirmation dialog when the part was marked as installed in service
              this.pendingDeleteData = { request, deviceId: device.id, wearingPart };
              this.confirmUncheckDialogOpened = true;
            } else {
              this.deviceStore.deleteServicePartEffect({request, deviceId: device.id});
            }
          }
        }
      }
    }
  }  

  public confirmUncheckDialogClose(status: string): void {
    this.confirmUncheckDialogOpened = false;
    if (status === 'confirm' && this.pendingDeleteData) {
      const { request, deviceId } = this.pendingDeleteData;
      this.deviceStore.deleteServicePartEffect({ request, deviceId });
    } else if (this.pendingDeleteData) {
      // Reset the checkbox state
      this.pendingDeleteData.wearingPart.isChecked = true;
    }
    this.pendingDeleteData = null;
    this.dialogData = {};
  }

  public servicePartDialogClose(status: string): void {
    this.servicePartDialogOpened = false;
    const { wearingPartId, device, amount } = this.dialogData;
    const wearingPart = device.wearingParts.find((part: any) => part.id === wearingPartId);

    if (status === 'confirm') {
      const serviceId = device.services[0].id;
      this.handleServicePartConfirm(wearingPart, device);

      /*
      this.onCheckboxChange(wearingPartId, serviceId, device);
      if (wearingPart.quantity != this.amount) {
        var request: IUpdateWearingPartRequest = {
          id: wearingPart.id,
          wearingPartCategoryId: wearingPart.wearingPartCategory.id,
          deviceId: device.id,
          quantity: this.amount
        }

        this.deviceStore.updateWearingPartEffect({request, deviceId: device.id});
      }
        */
    } else {
      // Reset the state of the checkbox if dialog is canceled
      if (wearingPartId) {
        if (wearingPart) {
          wearingPart.isChecked = false; // Reset the checkbox state
        }
      }
    }
    this.dialogData = {}; // Reset dialog data
  }

  onCheckboxChange(wearingPartId: string, serviceId: string, device: any): void {
    var request: IAddServicePart = {
      wearingPartId: wearingPartId,
      serviceId: serviceId,
      deviceId: device.id,
      revisionId: this.revisionId
    }
    this.deviceStore.addServicePartEffect({request, deviceId: device.id});
  }

  public handleServicePartConfirm(wearingPart: any, device: any): void {
    const needsUpdate = wearingPart.quantity != this.amount;
    const serviceId = device.services[0].id;
  
    if (needsUpdate) {
      const updateRequest: IUpdateWearingPartRequest = {
        id: wearingPart.id,
        wearingPartCategoryId: wearingPart.wearingPartCategory.id,
        deviceId: device.id,
        quantity: this.amount
      };
  
      this.deviceStore.updateWearingPart(updateRequest, device.id, () => {
        this.onCheckboxChange(wearingPart.id, serviceId, device);
      });
    } else {
      this.onCheckboxChange(wearingPart.id, serviceId, device);
    }
  }  

  /////////////////
  // Pagination //
  ///////////////

  // function to load devices after pagination changes
  loadDevices(): void {
    this.deviceStore.getDevicesWithWearingPartsByRevision({
      revisionId: this.revisionId,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
    });
  }
  
  // pagination function
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadDevices();
    }
  }

  // pagination function
  nextPage(): void {
    if (this.hasNextPage) {
      this.currentPage++;
      this.loadDevices();
    }
  }

  // pagination function
  previousPage(): void {
    if (this.hasPreviousPage) {
      this.currentPage--;
      this.loadDevices();
    }
  }

  // pagination function
  onPageSizeChange(newSize: number): void {
    if (this.pageSize === newSize) return;
    
    this.pageSize = newSize;
    this.currentPage = 1;

    this.loadDevices();
  }

  // Helper method to generate page numbers for pagination UI
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }  
}
